import { Component, ElementRef, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { ReportDesignerDocumentComponent } from '../report-designer-document/report-designer-document.component';
import { ReportDesignerDropTarget } from '../../models/report-designer-drop-target';
import { ReportDesignerPage } from '../../models/report-designer-page';
import { ReportDesignerPageOptions } from '../../models/report-designer-page-options';
import { GridsterHelper } from '../../util/gridster-helper';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { WidgetLayoutManagerService } from '../../services/widget-layout-manager.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { ObjectUtil } from '../../../../components/common/object-util/object-util';
import { ReportDesignerWidgetConfig } from '../../models/report-designer-widget-config';
import { GridsterItem } from 'angular-gridster2';
import { ReportDesignerService } from '../../services/report-designer.service';
import { AppLocalizationService } from '../../../../services/AppLocalizationService';
import { AppSettingsService } from '../../../../services/app-settings.service';
import { environment } from '../../../../../environments/environment';

/**
 * Report designer page
 */
@Component({
  selector: 'report-designer-page',
  templateUrl: './report-designer-page.component.html',
  styleUrls: ['./report-designer-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportDesignerPageComponent extends DestroyBase implements OnInit {
  /**
   * used only in HTML to display page number
   */
  @Input() public footerPageNumber: number;

  /**
   * The page
   */
  @Input() public page: ReportDesignerPage;

  /**
   * The document
   */
  @Input() public document: ReportDesignerDocumentComponent;

  /**
   * The RD host component
   */
  @Input() public main: ReportDesignerHostComponent;

  /**
   * Gets the edit mode
   */
  public get editMode() {
    return this.rdStorage.getAppEditMode();
  }

  /**
   * Gets the page index
   */
  public get pageIndex() {
    return this.document.data?.pages?.indexOf(this.page);
  }

  /**
   * Is it a virtual page?
   */
  public isVirtualPage = false;

  /**
   * Widget drop target is enabled?
   */
  public widgetDropTargetEnabled = false;

  /**
   * Maximum free space available? Widget can be dropped in full size
   */
  public maxFreeSpaceAvailable = false;

  /**
   * Minimum free space available? Widget can be dropped in minimum size
   */
  public minFreeSpaceAvailable = false;

  /**
   * Mouse is inside the drop target?
   */
  public isMouseInTarget = false;

  /**
   * Widget drop target: Preview, where the widget will be dropped
   */
  public dropTarget: ReportDesignerDropTarget = new ReportDesignerDropTarget();

  /**
   * Active widget index
   */
  public activeWidgetIndex: number = null;

  /**
   * A list of widgets on this page
   */
  private _widgets: ReportDesignerWidgetConfig[] = [];

  /**
   * Helper tuple that holds page and widgets options
   */
  private _gridsterOptions: [ReportDesignerPageOptions, ReportDesignerWidgetConfig[]] = [undefined, undefined];

  /**
   * Page options
   */
  _options: ReportDesignerPageOptions;

  /**
   * Gets the page options
   */
  get options(): ReportDesignerPageOptions {
    return this._options;
  }

  /**
   * Sets the page options
   */
  @Input() set options(value: ReportDesignerPageOptions) {
    this._gridsterOptions[0] = value;
    if (this._gridsterOptions[1]) {
      //when both page and widgets options are set update options for Gridster2
      this.updateConfigsForGridster2();
    }
  }

  /**
   * Gets the widget on this page
   */
  get widgets(): ReportDesignerWidgetConfig[] {
    return this._widgets;
  }

  /**
   * Sets the widgets on this page
   */
  @Input() set widgets(value: ReportDesignerWidgetConfig[]) {
    this._gridsterOptions[1] = value;
    if (this._gridsterOptions[0]) {
      //when both page and widgets options are set update options for Gridster2
      this.updateConfigsForGridster2();
    }
  }

  /**
   * Update the config from the AngularJS Gridster version to the Angular Gridster version
   */
  private updateConfigsForGridster2(): void {
    if (this._gridsterOptions[0] && this._gridsterOptions[1]) {
      const pageOptions = this._gridsterOptions[0];
      const widgetsConfigs = this._gridsterOptions[1];

      GridsterHelper.updatePageConfigForGridster2(pageOptions);
      for (const widget of widgetsConfigs) {
        GridsterHelper.updateWidgetForGridster2(widget, pageOptions);
      }

      //only when updated set properties
      this._options = pageOptions;
      this._widgets = widgetsConfigs;
    }
  }

  /**
   * Constructor
   * @param rdStorage
   * @param widgetLayoutManager
   * @param eventBus
   * @param _reportService
   * @param _appLocalization
   * @param appSettings
   * @param elementRef
   */
  constructor(private rdStorage: ReportDesignerStorageService,
    private widgetLayoutManager: WidgetLayoutManagerService,
    private eventBus: EventBusService,
    private _reportService: ReportDesignerService,
    private _appLocalization: AppLocalizationService,
    private appSettings: AppSettingsService,
    public elementRef: ElementRef) {
    super();
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.options.resizable.stop = (widget: GridsterItem) => { this.onResizableStop(widget as ReportDesignerWidgetConfig); };

    //If returning from localization page
    this.loadTemplate();

    if (this.options.isOnDisplay == null) {
      this.options.isOnDisplay = true; //when landscape page is selected all other pages are hidden by default
    }
    if (this.options.isHidden == null) {
      this.options.isHidden = false; //when hidden using "eye" button
    }
    this.options.isPortrait = this.options.name.indexOf("-H") === -1;

    //we need page to have same min/max rows, so "drop target" can access whole page
    this.options.minRows = this.options.maxRows;

    this.options.mobileModeEnabled = false;

    //if not paged printing call pageDataLoaded if there are no widgets
    //for paged print pagedPrint.pagePrintingStart event will raised
    if (this.widgets.length === 0 && !this.options.waitingForPrint) {
      this.document.pageDataLoaded(this);
    }

    this.onEvents.push(this.eventBus.subscribeEvent('pagedPrint.pagePrintingStart', (page: ReportDesignerPage) => {
      if (this.page === page && this.widgets.length === 0) {
        this.document.pageDataLoaded(this);
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('activatePage', (index) => {
      if (this.pageIndex === index) {
        this.activatePage();
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent("scrollToWidget", (params) => {
      const widgetGuid = params.widgetGuid;
      const widgetPage = params.widgetPage;

      if (this.options.id !== widgetPage.options.id) return;//handle just once, on page where widget is

      this.document.scrollToWidget(widgetPage, widgetGuid);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent("getPageViewModelByPageId", (params) => {
      if (this.options.id === params.id) {
        params.callback(this);
      }
    }));
  }

  /**
   * Resizable stop handler
   * @param widgetConfig
   */
  public onResizableStop(widgetConfig: ReportDesignerWidgetConfig): void {
    this.eventBus.broadcastEvent('widgetResized', widgetConfig);
  }

  /**
   * Clears the page
   */
  public clear(): void {
    this.widgets = [];
    this.document.main.setActivePage(this.page, false);
    this.document.main.setActiveWidget(null);
  }

  /**
   * Adds a widget
   * @param widget The widget that should be added
   */
  private addWidget(widget: ReportDesignerWidgetConfig): void {
    GridsterHelper.updateWidgetForGridster2(widget, this._options);
    this.activeWidgetIndex = this.widgets.push(widget) - 1;
  }

  /**
   * Widget data loaded handler
   * @param widgetId
   */
  public widgetDataLoaded(widgetId: string): void {
    let pageLoaded = true;

    for (const widget of this.widgets) {
      if (widget.widgetSettings.id === widgetId) {
        if (!widget.dataLoaded) {
          widget.dataLoaded = true;
          !environment.production && console.log('Page ' + (this.pageIndex + 1) + ': Loaded widget: ' + widget.template);
        } else {
          console.warn(`Widget ${widgetId}: widgetDataLoaded already called!!`);
        }
      }

      pageLoaded = pageLoaded && widget.dataLoaded;
    }

    if (pageLoaded && !this.page.dataLoaded) {
      this.page.dataLoaded = true;
      console.info(`Loading page done: ${(this.pageIndex + 1)}`);
      this.document.pageDataLoaded(this);
    }
  }

  /**
   * Widget data saved handler
   * @param widgetId
   */
  public widgetDataSaved(widgetId: string): void {
    setTimeout(() => {
      let pageSaved = true;
      for (const widget of this.widgets) {
        if (widget.widgetSettings.id === widgetId) {
          widget.dataSaved = true;
        }

        pageSaved = pageSaved && widget.dataSaved;
      }

      if (pageSaved) {
        this.page.dataSaved = true;
        this.document.pageDataSaved();
      }
    });
  }

  /**
   * Activates the current page
   */
  public activatePage(): void {
    this.document.main.setActivePage(this.page, false);

    const hideAllOthers = !this.options.isPortrait;
    const showOnlyPortrait = this.options.isPortrait;

    for (const page of this.document.data.pages) {
      if (page.options === this.options) {
        page.options.isOnDisplay = true;
      } else if (hideAllOthers) {
        page.options.isOnDisplay = false;
      } else if (page.options.isPortrait && showOnlyPortrait) {
        page.options.isOnDisplay = true;
      } else {
        page.options.isOnDisplay = false;
      }
    }

    this.document.scrollToPage(this.page);
  }

  /**
   * Grid mode enabled?
   * @returns
   */
  public isGridModeEnabled(): boolean {
    return this.rdStorage.getAppWidgetHeaderMode() && this.rdStorage.getAppReportGridRasterMode()
  }

  /**
   * Page entered event handler
   */
  public pageEntered(): void {
    if (this.rdStorage.getWidgetTargetDropMode()) {
      this.startDropWidgetTarget(this.rdStorage.getWidgetTargetDropModeOptions());
    }
  }

  /**
   * Page left event handler
   */
  public pageLeft(): void {
    this.widgetDropTargetEnabled = false;
  }

  /**
   * Starts the widget drop target
   * @param config
   */
  public startDropWidgetTarget(config: ReportDesignerWidgetConfig): void {
    this.widgetDropTargetEnabled = true;
    this.maxFreeSpaceAvailable = false;
    this.minFreeSpaceAvailable = false;
    this.isMouseInTarget = false;
    const dtOptions = this.dropTarget;
    dtOptions.top = 0;
    dtOptions.left = 0;

    const margins = this.options.margins || [];//page option
    dtOptions.distanceW = margins[1] || 0;
    dtOptions.distanceH = margins[0] || 0;

    dtOptions.sizeX = config.sizeX;
    dtOptions.sizeY = config.sizeY;
    dtOptions.width = dtOptions.sizeX * this.options.colWidth - dtOptions.distanceW;
    dtOptions.height = dtOptions.sizeY * this.options.rowHeight - dtOptions.distanceH;

    dtOptions.minSizeX = config.minSizeX || config.sizeX;
    dtOptions.minSizeY = config.minSizeY || config.sizeY;
    dtOptions.minWidth = Math.min(dtOptions.minSizeX * this.options.colWidth - dtOptions.distanceW, dtOptions.width);
    dtOptions.minHeight = Math.min(dtOptions.minSizeY * this.options.rowHeight - dtOptions.distanceH, dtOptions.height);

    dtOptions.lastGridsterCol = -1;
    dtOptions.lastGridsterRow = -1;
  }

  /**
   * Mouse entered the drop target event handler
   */
  public mouseEnteredTarget(): void {
    this.isMouseInTarget = true;
  }

  /**
   * Mouse left the drop target event handler
   */
  public mouseLeftTarget(): void {
    this.isMouseInTarget = false;
  }

  /**
   * Mouse moved event handler
   * @param $event
   * @returns
   */
  public mouseMove($event): void {
    if (!this.widgetDropTargetEnabled) {
      return;
    }
    $event.stopPropagation();//don't bubble

    let x = 0, y = 0;

    let target = $event.currentTarget;
    if (target.className.indexOf("widgetDropTarget") !== -1) {
      target = target.previousElementSibling;//page div
      x = $event.offsetX + this.dropTarget.left;
      y = $event.offsetY + this.dropTarget.top;
    } else if (target.className.indexOf("gridster-container") !== -1) {
      const el = target.getBoundingClientRect();
      x = $event.clientX - el.left;
      y = $event.clientY - el.top;
    }
    else {
      return;
    }

    const w = target.clientWidth;
    const h = target.clientHeight;
    x = Math.min(x, w);
    y = Math.min(y, h);

    let gridsterCol = Math.floor(x / this.options.colWidth);
    let gridsterRow = Math.floor(y / this.options.rowHeight);
    gridsterCol = Math.min(gridsterCol, this.options.columns - this.dropTarget.minSizeX);
    gridsterRow = Math.min(gridsterRow, this.options.maxRows - this.dropTarget.minSizeY);

    if (this.dropTarget.lastGridsterCol != gridsterCol || this.dropTarget.lastGridsterRow != gridsterRow) {
      this.dropTarget.left = Math.floor(gridsterCol * this.options.colWidth) + this.dropTarget.distanceW;
      this.dropTarget.top = Math.floor(gridsterRow * this.options.rowHeight) + this.dropTarget.distanceH;
      this.dropTarget.lastGridsterCol = gridsterCol;
      this.dropTarget.lastGridsterRow = gridsterRow;

      this.getConfigForDropWidget();//force calcluating this.maxFreeSpaceAvailable and  this.minFreeSpaceAvailable
    }
  }

  /**
   * Get the widget drop target config
   * @returns
   */
  public getConfigForDropWidget(): ReportDesignerWidgetConfig {
    let config = this.rdStorage.getWidgetTargetDropModeOptions();
    config = ObjectUtil.deepCopy(config); //Deep copy
    config.col = this.dropTarget.lastGridsterCol;
    config.row = this.dropTarget.lastGridsterRow;

    const pageArray = this.widgetLayoutManager.createPageArray(this.options.columns, this.options.maxRows, this.widgets);
    this.minFreeSpaceAvailable = false;
    this.maxFreeSpaceAvailable = this.widgetLayoutManager.checkFreeSpace(pageArray, config.col, config.row, config.sizeX, config.sizeY);
    if (!this.maxFreeSpaceAvailable) {
      //try with min values
      config.sizeX = config.minSizeX;
      config.sizeY = config.minSizeY;
      this.minFreeSpaceAvailable = this.widgetLayoutManager.checkFreeSpace(pageArray, config.col, config.row, config.sizeX, config.sizeY);
    }

    return (this.maxFreeSpaceAvailable || this.minFreeSpaceAvailable) ? config : null;
  }

  /**
   * The widget drop target was clicked
   * @returns
   */
  public widgetDropTargetClicked(): void {
    if (!this.rdStorage.getWidgetTargetDropMode()) {
      return;
    }
    const config = this.getConfigForDropWidget();
    if (config) {
      this.addWidget(config);
      this.widgetDropTargetEnabled = false;
      this.rdStorage.setWidgetTargetDropMode(false, null);

      //new pages might need update of header mode
      const headerMode = this.rdStorage.getAppWidgetHeaderMode();
      ReportDesignerPage.applyWidgetHeaderMode(this.page, headerMode);

      this.eventBus.broadcastEvent('widgetsChanged');
    }
  }

  /**
   * Page overlay clicked event handler
   */
  public pageOverlayClicked(): void {
    if (this.document.main.getDocumentClasses().indexOf("ReportOverviewMode") !== -1) {//double check
      this.document.main.removeDocumentClass("ReportOverviewMode");
      setTimeout(() => {//Update layout
        this.document.main.setActivePage(this.page, true);
      });
    }
  }

  /**
   * On load template action
   */
  public loadTemplate(): void {
    //check if template and localization is saved in session storage when returning from localization page
    const template = JSON.parse(sessionStorage.getItem('template'));
    const localization = JSON.parse(sessionStorage.getItem('localization'));
    if (!template || !localization) return;

    const templateId = template.Oid;
    const languageIds = template.templateLanguages;
    const templateLanguagesConfigurations = this.appSettings.allReportDesignerLocales.filter(l => languageIds.indexOf(l.id) !== -1);
    const firstTemplateLanguageId = languageIds[0];

    this._reportService.singleTemplate(templateId).subscribe(data => {
      if (data) {
        const documentJson = JSON.parse(data.Json);
        this.document.clear();
        this._appLocalization.setReportDesignerDictionaryConfigurations(templateLanguagesConfigurations, false);
        this._appLocalization.setReportDesignerDictionaryConfigById(firstTemplateLanguageId).then(() => {
          this.rdStorage.setTemplate(data);
          //inform some change occured, so app can react (f.e. adjust dropdown languages in RD)
          this.eventBus.broadcastEvent('rd:dictionaryConfigIdSet');

          setTimeout(() => {
            this.document.loadDocument(documentJson);
            this.eventBus.broadcastEvent("templateLoaded");
            this.eventBus.broadcastEvent("enableButtonsForLoadDocument");
            this.eventBus.broadcastEvent("openConfirmDialog", {
              success: () => {
                //Nothing
              }
            }, 'templateModeDialog');
            sessionStorage.removeItem('template');
            sessionStorage.removeItem('localization');
          }, 0);
        });
      }
    })
  }

  /**
   * trackby helper for *ngFor
   * @param _index Index
   * @returns 
   */
  public trackByWidgetId(_index: number, widget: ReportDesignerWidgetConfig): string {
    return widget?.widgetSettings?.id;
  }

  public detectChanges(): void {
    this.options.api.optionsChanged();
  }
}
